// sqlFilterUtils.js
// Build WHERE/HAVING from FilterItem[] and inject into SQL.
// Supports both tagless auto-injection and explicit tags:
//   <|TableName|>
//   <|Where|>, <|WhereDefault|>, <|WhereVisual|>, <|WhereGlobal|>
//   <|Having|>, <|HavingDefault|>, <|HavingVisual|>, <|HavingGlobal|>

const reWord = (w) => new RegExp(`\\b${w}\\b`, 'i');
const rxWhere  = reWord('where');
const rxHaving = reWord('having');
const rxGroup  = reWord('group');
const rxOrder  = reWord('order');
const rxLimit  = reWord('limit');

export const quoteIdent = (name) => `"${String(name).replace(/"/g, '""')}"`;

const toSqlLiteral = (v) => {
  if (v === null || v === undefined) return 'NULL';
  if (v instanceof Date && !isNaN(v)) return `'${v.toISOString().replace(/'/g, "''")}'`;
  const t = typeof v;
  if (t === 'number' && Number.isFinite(v)) return String(v);
  if (t === 'boolean') return v ? 'TRUE' : 'FALSE';
  return `'${String(v).replace(/'/g, "''")}'`;
};

const parseValue = (raw) => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== 'string') return raw;
  const s = raw.trim();
  if (!s) return '';
  if (s.startsWith('[') && s.endsWith(']')) { try { return JSON.parse(s); } catch {} }
  if (s === 'true') return true;
  if (s === 'false') return false;
  if (s === 'null') return null;
  const n = Number(s);
  if (Number.isFinite(n) && String(n) === s) return n;
  return s;
};

const buildPredicate = ({ Column, Operator, Value }) => {
  if (!Column || !Operator) return null;
  const col = quoteIdent(Column);
  const op  = String(Operator).toLowerCase();
  const v   = parseValue(Value);

  switch (op) {
    case 'isnull':  return `${col} IS NULL`;
    case 'notnull': return `${col} IS NOT NULL`;

    case 'eq':   return v === null ? `${col} IS NULL`     : `${col} = ${toSqlLiteral(v)}`;
    case 'neq':  return v === null ? `${col} IS NOT NULL` : `${col} <> ${toSqlLiteral(v)}`;
    case 'lt':   return `${col} < ${toSqlLiteral(v)}`;
    case 'lte':  return `${col} <= ${toSqlLiteral(v)}`;
    case 'gt':   return `${col} > ${toSqlLiteral(v)}`;
    case 'gte':  return `${col} >= ${toSqlLiteral(v)}`;

    case 'contains':   return `${col} LIKE ${toSqlLiteral(`%${v}%`)}`;
    case 'startswith': return `${col} LIKE ${toSqlLiteral(`${v}%`)}`;
    case 'endswith':   return `${col} LIKE ${toSqlLiteral(`%${v}`)}`;

    case 'in': {
      const arr = Array.isArray(v) ? v : [v];
      if (arr.length === 0) return null;
      return `${col} IN (${arr.map(toSqlLiteral).join(',')})`;
    }
    case 'notin': {
      const arr = Array.isArray(v) ? v : [v];
      if (arr.length === 0) return null;
      return `${col} NOT IN (${arr.map(toSqlLiteral).join(',')})`;
    }
    case 'between': {
      const a = Array.isArray(v) ? v : [];
      if (a.length !== 2) return null;
      return `${col} BETWEEN ${toSqlLiteral(a[0])} AND ${toSqlLiteral(a[1])}`;
    }
    default:
      return null;
  }
};

/** AND-join predicates without leading WHERE/HAVING. */
export function buildClause(items = []) {
  const parts = [];
  for (const it of items) {
    const p = buildPredicate(it || {});
    if (p) parts.push(p);
  }
  return parts.length ? parts.join(' AND ') : '';
}

/**
 * Back-compat: inject WHERE/HAVING (no tags required).
 * - WHERE: append to existing WHERE; else insert before GROUP/ORDER/LIMIT/EOL.
 * - HAVING: only if GROUP BY present; append to existing HAVING; else insert.
 */
export function injectWhereHaving(sql, { whereAll = '', havingAll = '' } = {}) {
  let out = String(sql);

  // WHERE
  if (whereAll) {
    if (rxWhere.test(out)) {
      out = out.replace(
        /(\bWHERE\b)([\s\S]*?)(\bGROUP\b|\bORDER\b|\bLIMIT\b|$)/i,
        (m, w, body, tail) => {
          const trimmed = body.trim();
          const glue = trimmed ? `${trimmed} AND (${whereAll})` : `(${whereAll})`;
          return `${w} ${glue} ${tail || ''}`;
        }
      );
    } else {
      const idxOrder = out.search(rxOrder);
      const idxGroup = out.search(rxGroup);
      const idxLimit = out.search(rxLimit);
      const cut = [idxOrder, idxGroup, idxLimit].filter(i => i >= 0).sort((a,b)=>a-b)[0];
      out = cut >= 0
        ? `${out.slice(0, cut)} WHERE ${whereAll} ${out.slice(cut)}`
        : `${out} WHERE ${whereAll}`;
    }
  }

  // HAVING (needs GROUP BY)
  if (havingAll && rxGroup.test(out)) {
    if (rxHaving.test(out)) {
      out = out.replace(
        /(\bHAVING\b)([\s\S]*?)(\bORDER\b|\bLIMIT\b|$)/i,
        (m, h, body, tail) => {
          const trimmed = body.trim();
          const glue = trimmed ? `${trimmed} AND (${havingAll})` : `(${havingAll})`;
          return `${h} ${glue} ${tail || ''}`;
        }
      );
    } else {
      const idxOrder = out.search(rxOrder);
      const idxLimit = out.search(rxLimit);
      const cut = [idxOrder, idxLimit].filter(i => i >= 0).sort((a,b)=>a-b)[0];
      out = cut >= 0
        ? `${out.slice(0, cut)} HAVING ${havingAll} ${out.slice(cut)}`
        : `${out} HAVING ${havingAll}`;
    }
  }

  return out;
}

/**
 * Expand template tags if present (per-layer & combined). Falls back to auto-injection.
 * options:
 *  - tableName: string
 *  - where:  { default?: string, visual?: string, global?: string, combined?: string }
 *  - having: { default?: string, visual?: string, global?: string, combined?: string }
 */
export function compileSql(template, options = {}) {
  const {
    tableName,
    where = {},
    having = {},
  } = options;

  let sql = String(template || '');

  // 1) TableName
  if (tableName) {
    sql = sql.replace(/<\|TableName\|>/g, quoteIdent(tableName));
  }

  // Build combined strings (for convenience if caller only passed per-layers)
  const whereDefault = (where.default || '').trim();
  const whereVisual  = (where.visual  || '').trim();
  const whereGlobal  = (where.global  || '').trim();
  const whereCombined = (where.combined || [whereDefault, whereVisual, whereGlobal].filter(Boolean).join(' AND ')).trim();

  const havingDefault = (having.default || '').trim();
  const havingVisual  = (having.visual  || '').trim();
  const havingGlobal  = (having.global  || '').trim();
  const havingCombined = (having.combined || [havingDefault, havingVisual, havingGlobal].filter(Boolean).join(' AND ')).trim();

  // Quick presence flags
  const hasWhereTagCombined = /<\|Where\|>/g.test(sql);
  const hasHavingTagCombined = /<\|Having\|>/g.test(sql);
  const hasAnyWhereLayerTag = /<\|Where(Default|Visual|Global)\|>/g.test(sql);
  const hasAnyHavingLayerTag = /<\|Having(Default|Visual|Global)\|>/g.test(sql);

  // 2) Combined Where/Having tags (take precedence if present)
  if (hasWhereTagCombined) {
    sql = sql.replace(/<\|Where\|>/g, whereCombined ? `WHERE ${whereCombined}` : '');
  }
  if (hasHavingTagCombined) {
    // Only render HAVING if query groups
    if (rxGroup.test(sql)) {
      sql = sql.replace(/<\|Having\|>/g, havingCombined ? `HAVING ${havingCombined}` : '');
    } else {
      sql = sql.replace(/<\|Having\|>/g, '');
    }
  }

  // 3) Per-layer Where tags
  if (hasAnyWhereLayerTag) {
    const layerPairs = [
      { tag: '<|WhereDefault|>', clause: whereDefault },
      { tag: '<|WhereVisual|>',  clause: whereVisual },
      { tag: '<|WhereGlobal|>',  clause: whereGlobal },
    ];

    // Replace left-to-right; for the first non-empty at a position where no WHERE precedes, emit WHERE; else AND(...)
    let insertedWhere = false;
    const firstWhereIdx = sql.search(rxWhere);

    // Callback replacement (left-to-right)
    for (const { tag, clause } of layerPairs) {
      sql = sql.replace(new RegExp(tag.replace(/[|<>]/g, (m)=>'\\'+m), 'g'), (m, offset) => {
        if (!clause) return ''; // remove empty layer tag
        const hasWhereBefore = firstWhereIdx >= 0 && firstWhereIdx < offset;
        if (hasWhereBefore || insertedWhere) {
          return ` AND (${clause})`;
        }
        // No WHERE before this tag → start WHERE here
        insertedWhere = true;
        return ` WHERE ${clause}`;
      });
    }
  }

  // 4) Per-layer Having tags
  if (hasAnyHavingLayerTag) {
    const layerPairs = [
      { tag: '<|HavingDefault|>', clause: havingDefault },
      { tag: '<|HavingVisual|>',  clause: havingVisual },
      { tag: '<|HavingGlobal|>',  clause: havingGlobal },
    ];

    const hasGroup = rxGroup.test(sql);
    if (hasGroup) {
      let insertedHaving = false;
      const firstHavingIdx = sql.search(rxHaving);

      for (const { tag, clause } of layerPairs) {
        sql = sql.replace(new RegExp(tag.replace(/[|<>]/g, (m)=>'\\'+m), 'g'), (m, offset) => {
          if (!clause) return '';
          const hasHavingBefore = firstHavingIdx >= 0 && firstHavingIdx < offset;
          if (hasHavingBefore || insertedHaving) {
            return ` AND (${clause})`;
          }
          insertedHaving = true;
          return ` HAVING ${clause}`;
        });
      }
    } else {
      // No GROUP BY → strip having layer tags
      sql = sql.replace(/<\|Having(Default|Visual|Global)\|>/g, '');
    }
  }

  // 5) If no tags were used for WHERE/HAVING, fall back to auto-injection
  if (!hasWhereTagCombined && !hasAnyWhereLayerTag && whereCombined) {
    sql = injectWhereHaving(sql, { whereAll: whereCombined });
  }
  if (!hasHavingTagCombined && !hasAnyHavingLayerTag && havingCombined) {
    sql = injectWhereHaving(sql, { havingAll: havingCombined });
  }

  return sql;
}

/**
 * Convenience: resolve table name + filters in one call.
 *   compileSql(template, {
 *     tableName: 'FactSales',
 *     where:  { default, visual, global, combined },
 *     having: { default, visual, global, combined }
 *   })
 */
export const prepareSql = compileSql;
